import type {
  IAffiliateProgramCreativeFormFieldsPayload,
} from '@modules/affiliate-program-creatives/submodules/edit/types';
import type { IContextMutationResult } from '@modules/core/services/query/types';
import {
  AFFILIATE_PROGRAM_CREATIVES_LIST_QUERY_KEY,
  AFFILIATE_PROGRAM_CREATIVES_PERMISSIONS,
} from '@modules/affiliate-program-creatives/constants';
import { useRestClient } from '@modules/core/services/api';
import { useContextMutation } from '@modules/core/services/query';
import { useToastStore } from '@modules/toast/store';
import {
  fileToBase64,
  getImageResolution,
} from '@utils/file';

import {
  AFFILIATE_PROGRAM_CREATIVES_SAVE_MUTATION_KEY,
  AFFILIATE_PROGRAM_CREATIVES_SAVE_URL,
} from '../constants';

export default function useAffiliateProgramCreativeSaveMutation(): IContextMutationResult<
  void,
  Error,
  IAffiliateProgramCreativeFormFieldsPayload
> {
  const { fetchPut, fetchPost } = useRestClient();
  const { displayPresetToast } = useToastStore();

  function getFetch(creative: IAffiliateProgramCreativeFormFieldsPayload): Promise<void> {
    if (creative.id) {
      return fetchPut<void>(
        AFFILIATE_PROGRAM_CREATIVES_SAVE_URL,
        { creative },
        {
          permission: AFFILIATE_PROGRAM_CREATIVES_PERMISSIONS.EDIT,
          urlParams: {
            id: creative.id,
          },
        },
      );
    }

    return fetchPost<void>(
      AFFILIATE_PROGRAM_CREATIVES_SAVE_URL,
      { creative },
      {
        permission: AFFILIATE_PROGRAM_CREATIVES_PERMISSIONS.CREATE,
        urlParams: {
          id: '',
        },
      },
    );
  }

  return useContextMutation<void, Error, IAffiliateProgramCreativeFormFieldsPayload>({
    mutationKey: [AFFILIATE_PROGRAM_CREATIVES_SAVE_MUTATION_KEY],
    mutationFn: async (creative) => {
      const promises: Promise<void>[] = [];
      const { imageUpload, ...restCreative } = creative;

      if (imageUpload?.length) {
        for (const file of imageUpload) {
          const newPromise = new Promise<void>((resolve, reject) => {
            fileToBase64(file)
              .then((contents) => {
                getImageResolution(contents).then(({ width, height }) => {
                  getFetch({
                    ...restCreative,
                    width,
                    height,
                    title: restCreative.title || file.name.split('.').slice(0, -1).join('.') || file.name,
                    banner: {
                      contents,
                      lastModified: file.lastModified,
                      name: file.name,
                      size: file.size,
                      type: file.type,
                      webkitRelativePath: file.webkitRelativePath,
                    },
                  }).then(resolve).catch(reject);
                }).catch(reject);
              })
              .catch(reject);
          });

          promises.push(newPromise);
        }
      } else {
        promises.push(getFetch(restCreative));
      }

      const results = await Promise.allSettled(promises);

      for (const result of results) {
        if (result.status === 'rejected') {
          throw result.reason;
        }
      }
    },
    async onSuccess({ queryClient, variables }) {
      await queryClient.invalidateQueries({ queryKey: [AFFILIATE_PROGRAM_CREATIVES_LIST_QUERY_KEY] });

      displayPresetToast(variables.id ? 'edited' : 'created');
    },
  });
}
